import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useBriefingCrewNames, useBriefingFlights } from "@/hooks/useBriefingFlights";
import { FlightLegRow } from "@/components/briefing/FlightLegRow";
import {
  FLIGHT_DIRECTION_LABEL,
  buildItineraries,
  formatDuration,
  layoverMinutes,
  routeLabel,
  type FlightItinerary,
  type FlightLeg,
} from "@produce/shared";

/**
 * Read-only Flights overview for the producer surfaces (review/publish
 * page and the portal briefing view), grouped per crew member — that is
 * how a producer checks the booking ("did Sanne get her return flight?").
 *
 * Deliberately NOT reused on the crew app: crew must see only their own
 * flights, which the crew app renders from its own scoped query. Here
 * RLS already hands the producer everything on their own briefing.
 *
 * Returns null when the section is off or empty, so callers can drop it
 * in unconditionally.
 */

function Layover({ previous, next }: { previous: FlightLeg; next: FlightLeg }) {
  const minutes = layoverMinutes(previous, next);
  if (minutes == null) return null;
  return (
    <p className="py-1 pl-4 text-xs text-muted-foreground">
      {formatDuration(minutes)} layover in {previous.arrival_airport || "transit"}
    </p>
  );
}

export function FlightsCard({
  briefingId,
  enabled,
}: {
  briefingId: string | null | undefined;
  enabled: boolean;
}) {
  const { data } = useBriefingFlights(enabled ? briefingId : null);

  const { data: crew = [] } = useBriefingCrewNames(briefingId, enabled);

  if (!enabled) return null;

  const withFlights = crew.filter((member) =>
    data.passengers.some((p) => p.briefing_crew_id === member.id),
  );
  if (withFlights.length === 0) return null;

  return (
    <Card className="mb-6 bg-card p-8">
      <h2 className="mb-6 text-2xl font-bold">Flights</h2>

      <p className="mb-6 text-sm text-muted-foreground">
        Each crew member sees only their own flight details in the app.
      </p>

      <div className="space-y-2">
        {withFlights.map((member) => (
          <CrewFlights
            key={member.id}
            name={member.name}
            itineraries={buildItineraries(data.legs, data.passengers, member.id)}
          />
        ))}
      </div>
    </Card>
  );
}

/**
 * One crew member's flights, collapsed by default.
 *
 * A production that flies ten people in produces ten itineraries of two
 * flights each — a wall of detail on a page a producer mostly scans.
 * The closed row carries the summary they actually scan for ("AMS → VIE,
 * VIE → AMS"), and opening one shows the full booking.
 */
function CrewFlights({ name, itineraries }: { name: string; itineraries: FlightItinerary[] }) {
  const [open, setOpen] = useState(false);
  const summary = itineraries
    .flatMap((itinerary) => itinerary.legs.map((entry) => routeLabel(entry.leg)))
    .join(" · ");

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3 text-left hover:bg-muted/50">
        <div className="min-w-0">
          <p className="font-semibold text-foreground">{name}</p>
          <p className="truncate text-sm text-muted-foreground">{summary}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 px-1 pt-3">
        {itineraries.map((itinerary) => (
          <div key={itinerary.direction}>
            <p className="mb-2 text-sm font-semibold text-muted-foreground">
              {FLIGHT_DIRECTION_LABEL[itinerary.direction]}
            </p>
            <div className="space-y-2">
              {itinerary.legs.map((entry, index) => (
                <div key={entry.leg.id}>
                  {index > 0 && (
                    <Layover previous={itinerary.legs[index - 1].leg} next={entry.leg} />
                  )}
                  <FlightLegRow leg={entry.leg} passenger={entry.passenger} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
